import { keepPreviousData, useInfiniteQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { getMessageReactions } from '../services/reaction.service';
import { messageKeys } from './messsage-keys';

export interface UseMessageReactionsParams {
  conversationId: string;
  messageId: string;
  bucketId: string;
  reactionFilter?: string;
  pageSize?: number;
  enabled?: boolean;
}

export interface MessageReactionsPageParam {
  pagingState?: string;
}

/**
 * Hook to fetch message reactions with pagination support
 */
export function useMessageReactions({
  conversationId,
  messageId,
  bucketId,
  reactionFilter,
  pageSize = 50,
  enabled = true
}: UseMessageReactionsParams) {
  const query = useInfiniteQuery({
    queryKey: messageKeys.reactions(conversationId, messageId, reactionFilter, pageSize),
    queryFn: async ({ pageParam }: { pageParam: MessageReactionsPageParam }) => {
      return await getMessageReactions(
        conversationId,
        messageId,
        bucketId,
        reactionFilter,
        pageSize,
        pageParam.pagingState
      );
    },
    initialPageParam: {} as MessageReactionsPageParam,
    getNextPageParam: (lastPage) => {
      if (!lastPage.hasMore || !lastPage.pagingState) return undefined;
      return { pagingState: lastPage.pagingState };
    },
    enabled: enabled && !!conversationId && !!messageId && !!bucketId,
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
    throwOnError: true,
    placeholderData: keepPreviousData
  });

  const loadMore = async () => {
    if (!query.hasNextPage || query.isFetchingNextPage) return;
    await query.fetchNextPage();
  };

  // Flatten all reactions from all pages
  const allReactions = query.data?.pages.flatMap((page) => page.reactions) || [];
  const totalCount = query.data?.pages[0]?.totalCount || 0;
  const hasMore = query.data?.pages[query.data.pages.length - 1]?.hasMore || false;

  // Memoize the flattened data
  const reactionsData = useMemo(
    () => ({
      reactions: allReactions,
      totalCount,
      hasMore
    }),
    [allReactions, totalCount, hasMore]
  );

  return {
    // Query state
    ...query,

    // Processed data
    ...reactionsData,

    // Actions
    loadMore,

    // Loading states
    isLoadingMore: query.isFetchingNextPage,
    canLoadMore: query.hasNextPage
  };
}
